import CubeIndex from '../cube/CubeIndex';
import {itemColor} from '../render/colors';
import {countHtml, escapeHtml, formatCount, formatValue, previousHtml, respondents} from '../render/format';
import {Cube, CubeCell, CubeCellValue, CubeMeasure, CubeMember, RenderContext, RenderSpec} from '../types';

export interface HierarchyNode {
    id: string;
    parentId?: string;
    depth: number;
    key: string;
    label: string;
    labels: string[];
    member: CubeMember;
    /** Colour of the first-level ancestor, inherited by the whole branch */
    rootColor: string;
    /** Set on deepest-level nodes only; inner nodes get their size from the children */
    leaf?: {cell: CubeCell; size: number; sizeCell: CubeCellValue; color?: CubeCellValue};
    total: number;
}

export interface Hierarchy {
    dimensionIds: string[];
    dimensionLabels: string[];
    sizeMeasure: CubeMeasure;
    colorMeasure?: CubeMeasure;
    nodes: HierarchyNode[];
}

export function pathChannel(cube: Cube, spec: RenderSpec): string[] {
    const path = spec.encoding.path;
    const ids = Array.isArray(path) ? path : typeof path === 'string' ? [path] : cube.dimensions.map(dimension => dimension.id);

    return ids.filter(id => cube.dimensions.some(dimension => dimension.id === id)).slice(0, 3);
}

/** Size is always the respondent count so the area stays meaningful whichever measure drives the colour. */
export function sizeMeasure(cube: Cube): CubeMeasure {
    return cube.measures.find(measure => measure.metric === 'count') ?? cube.measures[0];
}

export function sizeOf(cell: CubeCell, measure: CubeMeasure): number {
    const value = cell.values[measure.id];

    const counted = value?.value;

    return measure.metric === 'count' && counted !== null && counted !== undefined ? counted : respondents(value);
}

/** Sizes sum respondent counts; only a count measure may format them, any other would put its unit on a count */
export function formatSize(size: number, measure: CubeMeasure, ctx: RenderContext): string {
    return measure.metric === 'count' ? formatValue(size, measure, ctx) : formatCount({count: size} as CubeCellValue, false);
}

/** Tooltip head of any tree node: each path level named by its dimension, then the size */
export function nodeTooltip(node: HierarchyNode, hierarchy: Hierarchy, ctx: RenderContext, dot: boolean): string {
    const measure = hierarchy.sizeMeasure;
    const marker = dot ? `<span style="color:${node.rootColor}">●</span> ` : '';
    const path = node.labels.map((label, depth) => `${escapeHtml(hierarchy.dimensionLabels[depth])}: <b>${escapeHtml(label)}</b>`).join('<br>');

    return `${marker}${path}<br>${escapeHtml(measure.label)}: <b>${formatSize(node.leaf?.size ?? node.total, measure, ctx)}</b>`;
}

/** Low-base marker and previous term of a leaf size; a non-count size measure has no comparable previous count */
export function sizeDetailsHtml(node: HierarchyNode, measure: CubeMeasure, lowBase: boolean, ctx: RenderContext): string {
    const cell = node.leaf?.sizeCell;
    if (!cell) {
        return '';
    }

    return (lowBase ? ` ${countHtml(cell, true)}` : '') + (measure.metric === 'count' ? previousHtml(cell, measure, ctx) : '');
}

function mergeSizeCells(previous: CubeCellValue, cell: CubeCellValue, size: number): CubeCellValue {
    const n = previous.n === undefined && cell.n === undefined ? {} : {n: respondents(previous) + respondents(cell)};

    return {...cell, value: size, count: previous.count + cell.count, ...n};
}

/** Members are looked up per path level; total members and empty cells never enter the tree. */
export function buildHierarchy(cube: Cube, spec: RenderSpec, ctx: RenderContext): Hierarchy {
    const index = new CubeIndex(cube);
    const dimensionIds = pathChannel(cube, spec);
    const dimensionPositions = dimensionIds.map(id => index.dimensionIndex(id));
    const size = sizeMeasure(cube);
    const colorId = typeof spec.encoding.color === 'string' ? spec.encoding.color : undefined;
    const colorMeasure = colorId ? cube.measures.find(measure => measure.id === colorId) : undefined;
    const nodes = new Map<string, HierarchyNode>();
    const rootColors = new Map<string, string>();
    // Colour follows the member position in its dimension, so it does not shift with the members that have data
    const rootIndexOf = (cell: CubeCell) => cell.coords[dimensionPositions[0]];

    cube.cells.forEach(cell => {
        const members = dimensionPositions.map(position => cube.dimensions[position].members[cell.coords[position]]);
        const weight = sizeOf(cell, size);
        if (members.length === 0 || index.isOutsideTotal(cell, dimensionPositions) || members.some(member => !member || member.role === 'total') || weight <= 0) {
            return;
        }

        const keys: string[] = [];
        members.forEach((member, depth) => {
            keys.push(member.key);
            const id = JSON.stringify(keys);
            let node = nodes.get(id);
            if (!node) {
                if (depth === 0) {
                    rootColors.set(member.key, itemColor(member, undefined, rootIndexOf(cell), ctx));
                }
                node = {
                    id,
                    parentId: depth > 0 ? JSON.stringify(keys.slice(0, -1)) : undefined,
                    depth,
                    key: member.key,
                    label: member.label,
                    labels: members.slice(0, depth + 1).map(m => m.label),
                    member,
                    rootColor: rootColors.get(keys[0]) as string,
                    total: 0,
                };
                nodes.set(id, node);
            }
            node.total += weight;
            if (depth === members.length - 1) {
                // Cells outside the path dimensions (if any) collapse into the same leaf; the colour measure of the
                // first cell wins, which is only right while the validator binds every dimension to the path
                const previous = node.leaf;
                const sizeCell = cell.values[size.id];
                node.leaf = {
                    cell,
                    size: (previous?.size ?? 0) + weight,
                    sizeCell: previous ? mergeSizeCells(previous.sizeCell, sizeCell, previous.size + weight) : sizeCell,
                    color: previous?.color ?? (colorMeasure ? cell.values[colorMeasure.id] : undefined),
                };
            }
        });
    });

    const dimensionLabels = dimensionPositions.map(position => cube.dimensions[position].label);

    return {dimensionIds, dimensionLabels, sizeMeasure: size, colorMeasure, nodes: Array.from(nodes.values())};
}
